import "@/App.css";
import { Suspense, lazy, useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { CatalogProvider } from "@/context/CatalogContext";
import { ClientProvider, useClient } from "@/context/ClientContext";

import Layout from "@/components/Layout";
import CatalogRoute from "@/components/CatalogRoute";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import FirstAccess from "@/pages/FirstAccess";
import MyProfile from "@/pages/MyProfile";

import Dashboard from "@/pages/Dashboard";
import Cockpit360 from "@/pages/Cockpit360";
import Occurrences from "@/pages/Occurrences";
import OccurrenceDetail from "@/pages/OccurrenceDetail";
import OccurrenceTrash from "@/pages/OccurrenceTrash";
import Pendencies from "@/pages/Pendencies";
import ActionPlans from "@/pages/ActionPlans";
import ActionPlanDetail from "@/pages/ActionPlanDetail";
import ElectricRooms from "@/pages/ElectricRooms";
import GrainPests from "@/pages/GrainPests";
import DeviceMonitoring from "@/pages/DeviceMonitoring";
import EnvironmentIntelligence from "@/pages/EnvironmentIntelligence";
import EnvironmentCommunications from "@/pages/EnvironmentCommunications";
import Reports from "@/pages/Reports";
import AIChat from "@/pages/AIChat";

import ClientCentral from "@/pages/ClientCentral";
import Responsibles from "@/pages/Responsibles";

import OperationalConfig from "@/pages/OperationalConfig";
import AdminSettings from "@/pages/AdminSettings";
import PlatformAdmin from "@/pages/PlatformAdmin";
import DataAdmin from "@/pages/DataAdmin";
import VisualIdentity from "@/pages/VisualIdentity";
import MyAppearance from "@/pages/MyAppearance";
import Audit from "@/pages/Audit";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminCompanies from "@/pages/admin/AdminCompanies";
import AdminCompanyDetail from "@/pages/admin/AdminCompanyDetail";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminProfiles from "@/pages/admin/AdminProfiles";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminPermissions from "@/pages/admin/AdminPermissions";
import AccessDiagnostic from "@/pages/admin/AccessDiagnostic";

/*
 * Páginas que carregam o SDK cartográfico e o construtor de dashboards são
 * carregadas sob demanda (fora do bundle inicial).
 */
const GeoMap = lazy(() => import("@/pages/GeoMap"));
const OccurrenceWizard = lazy(() => import("@/pages/OccurrenceWizard"));
const DashboardDesigner = lazy(() => import("@/pages/admin/DashboardDesigner"));

function RouteFallback({ label = "Carregando…" }) {
  return (
    <div className="min-h-[320px] flex flex-col items-center justify-center gap-3" data-testid="geo-route-loading">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/** Área autenticada: só exige sessão; o acesso a cada módulo é do catálogo. */
function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * GATE DO FILTRO GLOBAL DE CLIENTES.
 *
 * As rotas autenticadas só renderizam quando a seleção global de clientes já
 * foi resolvida. Sem isso, o primeiro carregamento de um módulo poderia
 * consultar antes do contexto existir e exibir dados de TODOS os clientes
 * autorizados em vez do contexto ativo.
 *
 * Não bloqueia navegação nem recargas: o gate só espera a PRIMEIRA resolução
 * (segundos, normalmente milissegundos) e depois fica transparente.
 */
function ClientContextGate({ children }) {
  const { contextReady } = useClient();
  if (!contextReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="client-context-loading">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }
  return children;
}

/** Links antigos de e-mail (/finalizar-cadastro?token=...). */
function LegacyFinalizeRegistrationRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/primeiro-acesso", search: location.search }} replace />;
}

/*
 * Rotas do catálogo: [chave do catálogo, caminho, elemento, ação extra].
 * A chave é a MESMA de authorization/catalog.py; nenhuma regra de acesso
 * é definida no frontend.
 */
function catalogRoutes(theme, setTheme) {
  const geo = (element) => <Suspense fallback={<RouteFallback label="Carregando módulo…" />}>{element}</Suspense>;
  return [
    ["dashboard", "/", <Dashboard />],
    ["cockpit_360", "/cockpit", <Cockpit360 />],
    ["ocorrencias", "/ocorrencias", <Occurrences />],
    ["ocorrencias", "/ocorrencias/lixeira", <OccurrenceTrash />, { module: "ocorrencias", action: "restore" }],
    ["ocorrencias", "/ocorrencias/nova", geo(<OccurrenceWizard />), { module: "ocorrencias", action: "create" }],
    ["ocorrencias", "/ocorrencias/:id", <OccurrenceDetail />],
    ["pendencias", "/pendencias", <Pendencies />],
    ["plano_acao", "/planos-de-acao", <ActionPlans />],
    ["plano_acao", "/planos-de-acao/:id", <ActionPlanDetail />],
    ["salas_eletricas", "/salas-eletricas", <ElectricRooms />],
    ["pga", "/pragas-graos", <GrainPests />],
    ["monitoramento_dispositivos", "/monitoramento-dispositivos", <DeviceMonitoring />],
    ["central_geoespacial", "/central-geoespacial", geo(<GeoMap />)],
    ["inteligencia_ambiental", "/inteligencia-ambiental", <EnvironmentIntelligence />],
    ["comunicacao_alertas", "/inteligencia-ambiental/comunicacoes", <EnvironmentCommunications />],
    ["relatorios", "/relatorios", <Reports />],
    ["astral_ia", "/ai", <AIChat />],
    ["central_clientes", "/central-clientes", <ClientCentral />],
    ["central_clientes", "/central-clientes/:clientId", <ClientCentral />],
    ["responsaveis", "/responsaveis", <Responsibles />],
    ["configuracao_operacional", "/configuracao-operacional", <OperationalConfig />],
    ["administracao_sistema", "/administracao", <AdminSettings />],
    ["administracao_plataforma", "/administracao-plataforma", <PlatformAdmin />],
    ["administracao_dados", "/admin-dados", <DataAdmin />],
    ["identidade_visual_global", "/identidade-visual", <VisualIdentity />],
    ["minha_aparencia", "/minha-aparencia", <MyAppearance theme={theme} setTheme={setTheme} />],
    ["auditoria", "/auditoria", <Audit />],
  ];
}

/* Rotas antigas substituídas pela Central Administrativa. */
const LEGACY_REDIRECTS = [
  ["/usuarios", "/admin/permissoes/usuarios"],
  ["/central-de-acessos", "/admin/permissoes"],
  ["/empresas", "/admin/permissoes/clientes"],
  ["/admin/catalogo-modulos", "/admin/permissoes/catalogo"],
  ["/admin/dashboard-config", "/admin/dashboards"],
  ["/cadastros", "/central-clientes"],
];

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("pci-theme") || "light");

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("pci-theme", theme);
  }, [theme]);

  return (
    <BrandingProvider>
      <AuthProvider>
        <CatalogProvider>
          <ClientProvider>
            <BrowserRouter>
              <Routes>
                {/* Rotas públicas */}
                <Route path="/login" element={<Login />} />
                <Route path="/redefinir-senha" element={<ResetPassword />} />
                <Route path="/primeiro-acesso" element={<FirstAccess />} />
                <Route path="/finalizar-cadastro" element={<LegacyFinalizeRegistrationRedirect />} />

                {/* Área autenticada */}
                <Route
                  element={
                    <RequireAuth>
                      <ClientContextGate>
                        <Layout theme={theme} setTheme={setTheme} />
                      </ClientContextGate>
                    </RequireAuth>
                  }
                >
                  {catalogRoutes(theme, setTheme).map(([entry, path, element, action]) => (
                    <Route
                      key={path}
                      path={path}
                      element={<CatalogRoute entry={entry} action={action}>{element}</CatalogRoute>}
                    />
                  ))}

                  <Route path="/meu-perfil" element={<MyProfile />} />

                  {/* Central Administrativa */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverview />} />
                    <Route path="empresas" element={<CatalogRoute entry="empresas"><AdminCompanies /></CatalogRoute>} />
                    <Route path="empresas/:clientId" element={<CatalogRoute entry="empresas"><AdminCompanyDetail /></CatalogRoute>} />
                    <Route path="usuarios" element={<CatalogRoute entry="gestao_acessos"><AdminUsers /></CatalogRoute>} />
                    <Route path="usuarios/:userId" element={<CatalogRoute entry="gestao_acessos"><AdminUserDetail /></CatalogRoute>} />
                    <Route path="perfis" element={<CatalogRoute entry="gestao_acessos"><AdminProfiles /></CatalogRoute>} />
                    <Route path="catalogo" element={<CatalogRoute entry="catalogo_modulos"><AdminCatalog /></CatalogRoute>} />

                    {/* CENTRAL ÚNICA DE PERMISSÕES — único ponto de concessão
                        e revogação. Reutiliza os componentes canônicos. */}
                    <Route path="permissoes" element={<AdminPermissions />}>
                      <Route path="usuarios" element={<CatalogRoute entry="gestao_acessos"><AdminUsers /></CatalogRoute>} />
                      <Route path="usuarios/:userId" element={<CatalogRoute entry="gestao_acessos"><AdminUserDetail /></CatalogRoute>} />
                      <Route path="clientes" element={<CatalogRoute entry="empresas"><AdminCompanies /></CatalogRoute>} />
                      <Route path="clientes/:clientId" element={<CatalogRoute entry="empresas"><AdminCompanyDetail /></CatalogRoute>} />
                      <Route path="perfis" element={<CatalogRoute entry="gestao_acessos"><AdminProfiles /></CatalogRoute>} />
                      <Route path="catalogo" element={<CatalogRoute entry="catalogo_modulos"><AdminCatalog /></CatalogRoute>} />
                      <Route path="diagnostico" element={<CatalogRoute entry="gestao_acessos"><AccessDiagnostic /></CatalogRoute>} />
                    </Route>
                    {["dashboards", "dashboards/:dashboardId"].map((path) => (
                      <Route
                        key={path}
                        path={path}
                        element={
                          <CatalogRoute entry="design_dashboard">
                            <Suspense fallback={<RouteFallback label="Carregando construtor…" />}>
                              <DashboardDesigner />
                            </Suspense>
                          </CatalogRoute>
                        }
                      />
                    ))}
                  </Route>

                  {LEGACY_REDIRECTS.map(([from, to]) => (
                    <Route key={from} path={from} element={<Navigate to={to} replace />} />
                  ))}
                </Route>
              </Routes>
            </BrowserRouter>
          </ClientProvider>
        </CatalogProvider>
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrandingProvider>
  );
}

export default function AppWrapper() {
  return <App />;
}
